const DESKTOP_DELAY_MS = 400;
const LONGPRESS_DELAY_MS = 500;
const MOVE_CANCEL_PX = 10;
const VIEWPORT_MARGIN = 8;

let pop: HTMLDivElement | null = null;
let activeTarget: HTMLElement | null = null;
let outsideListening = false;

function ensurePop(): HTMLDivElement {
  if (pop !== null) {
    return pop;
  }
  const el = document.createElement("div");
  el.className = "tooltip-pop";
  el.style.display = "none";
  document.body.appendChild(el);
  pop = el;
  return el;
}

/**
 * Places the popup above the target, flipping below when it would clip the top
 * edge, then clamps both axes into the viewport. The popup must already be
 * display:block (so offsetWidth/Height read its laid-out size) before calling.
 */
function positionPop(el: HTMLDivElement, target: HTMLElement): void {
  const rect = target.getBoundingClientRect();
  const pw = el.offsetWidth;
  const ph = el.offsetHeight;
  let top = rect.top - ph - VIEWPORT_MARGIN;
  if (top < VIEWPORT_MARGIN) {
    top = rect.bottom + VIEWPORT_MARGIN;
  }
  let left = rect.left;
  const maxLeft = window.innerWidth - pw - VIEWPORT_MARGIN;
  if (left > maxLeft) {
    left = maxLeft;
  }
  if (left < VIEWPORT_MARGIN) {
    left = VIEWPORT_MARGIN;
  }
  const maxTop = window.innerHeight - ph - VIEWPORT_MARGIN;
  if (top > maxTop) {
    top = maxTop;
  }
  if (top < VIEWPORT_MARGIN) {
    top = VIEWPORT_MARGIN;
  }
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}

function onOutsidePointer(): void {
  hideTooltip();
}

function showTooltip(target: HTMLElement, text: string): void {
  if (text === "") {
    return;
  }
  const el = ensurePop();
  el.textContent = text;
  el.style.display = "block";
  activeTarget = target;
  positionPop(el, target);
  if (!outsideListening) {
    outsideListening = true;
    setTimeout((): void => document.addEventListener("pointerdown", onOutsidePointer, true), 0);
  }
}

export function hideTooltip(): void {
  if (pop !== null) {
    pop.style.display = "none";
  }
  activeTarget = null;
  if (outsideListening) {
    outsideListening = false;
    document.removeEventListener("pointerdown", onOutsidePointer, true);
  }
}

/**
 * Attaches a non-modal explanatory tooltip to el. getText is evaluated at show
 * time so it tracks language changes and driven state. Desktop shows on hover
 * after a delay; touch shows on long-press without consuming the tap — the
 * popup is pointer-events:none and pointerup is left untouched, so the row's
 * own input still fires. Empty getText() suppresses the popup entirely.
 */
export function attachTooltip(el: HTMLElement, getText: () => string): void {
  let hoverTimer: number | undefined;
  let pressTimer: number | undefined;
  let startX = 0;
  let startY = 0;

  el.addEventListener("pointerenter", (ev: PointerEvent): void => {
    if (ev.pointerType !== "mouse" || ev.buttons !== 0) {
      return;
    }
    window.clearTimeout(hoverTimer);
    hoverTimer = window.setTimeout((): void => showTooltip(el, getText()), DESKTOP_DELAY_MS);
  });
  el.addEventListener("pointerleave", (ev: PointerEvent): void => {
    if (ev.pointerType !== "mouse") {
      return;
    }
    window.clearTimeout(hoverTimer);
    if (activeTarget === el) {
      hideTooltip();
    }
  });

  const cancelPress = (): void => {
    window.clearTimeout(pressTimer);
    pressTimer = undefined;
  };
  el.addEventListener("pointerdown", (ev: PointerEvent): void => {
    if (ev.pointerType === "mouse") {
      return;
    }
    startX = ev.clientX;
    startY = ev.clientY;
    cancelPress();
    pressTimer = window.setTimeout((): void => showTooltip(el, getText()), LONGPRESS_DELAY_MS);
  });
  el.addEventListener("pointermove", (ev: PointerEvent): void => {
    if (pressTimer === undefined) {
      return;
    }
    const dx = ev.clientX - startX;
    const dy = ev.clientY - startY;
    if (dx * dx + dy * dy > MOVE_CANCEL_PX * MOVE_CANCEL_PX) {
      cancelPress();
    }
  });
  el.addEventListener("pointerup", cancelPress);
  el.addEventListener("pointercancel", cancelPress);
  el.addEventListener("contextmenu", (ev: Event): void => ev.preventDefault());
}
